import {
  dayjsWithTimezone,
  DD_FORMAT,
  DD_MMM_YYYY_FORMAT,
  DD_MMM_YYYY_HH_MM_A_FORMAT,
  DDD_FORMAT,
  DDD_MMM_DD_FORMAT,
  HH_MM_A_FORMAT,
  MMM_FORMAT,
  YYYY_FORMAT,
  YYYY_MM_DD_FORMAT,
} from '@healthhub/react-ui';
import type { ConfigType, Dayjs } from 'dayjs';

interface SortByDateStringProps {
  ascending?: boolean;
  dateA: string;
  dateB: string;
}

export const formattedDateDisplay = (date: ConfigType, formatter = DD_MMM_YYYY_FORMAT) => {
  if (!date) {
    return '';
  }
  if (!formatter) {
    return '';
  }
  const isValidDate = dayjsWithTimezone(date).isValid();

  if (isValidDate) {
    return dayjsWithTimezone(date).format(formatter);
  }

  return '-';
};

export const getDateLabel = (date: string) => {
  const today = dayjsWithTimezone().format(YYYY_MM_DD_FORMAT);
  const tomorrow = dayjsWithTimezone().add(1, 'day').format(YYYY_MM_DD_FORMAT);
  const parsedDate = dayjsWithTimezone(date).format(YYYY_MM_DD_FORMAT);

  if (parsedDate === today) {
    return 'Today';
  }
  if (parsedDate === tomorrow) {
    return 'Tomorrow';
  }
  return dayjsWithTimezone(date).format(DDD_MMM_DD_FORMAT);
};

export const getDateFromTimeStamp = (time: string) => {
  // time format '21:55:00'
  const d = new Date(); // Creates a Date Object using the clients current time
  const [hours, minutes, seconds] = time.split(':');
  d.setHours(+hours);
  d.setMinutes(+minutes);
  d.setSeconds(+seconds);
  return d;
};

export const isWithInCutOffTime = (startTime: string, endTime: string) => {
  try {
    const startDateTime = getDateFromTimeStamp(startTime);
    const endDateTime = getDateFromTimeStamp(endTime);
    const currentDateTime = new Date();
    if (endDateTime < startDateTime) {
      return currentDateTime >= startDateTime || currentDateTime <= endDateTime;
    }
    return currentDateTime >= startDateTime && currentDateTime <= endDateTime;
  } catch (e) {
    console.warn('An error occurred while checking if current time is within cut off time.', e);
    return false;
  }
};

export const getISODateFormat = (date: ConfigType) => formattedDateDisplay(date, YYYY_MM_DD_FORMAT);

export const dateDiffByMinutes = (startDate: Dayjs, endDate: Dayjs) =>
  dayjsWithTimezone(endDate).diff(startDate, 'minutes');

export const getSubtractDateMonths = (month: number, isFormat = true) =>
  isFormat
    ? dayjsWithTimezone().subtract(month, 'months').format(YYYY_MM_DD_FORMAT)
    : dayjsWithTimezone().subtract(month, 'months');

export const getSubtractDateDays = (days: number, isFormat = true, date?: ConfigType) => {
  const years = days >= 365 ? Math.floor(days / 365) : 0;
  const months = years ? 12 * years : Math.floor(days / 30);
  const remainingDays = years ? days - years * 365 : days - months * 30;

  const result = dayjsWithTimezone(date).subtract(months, 'months').subtract(remainingDays, 'days');

  return isFormat ? result.format(YYYY_MM_DD_FORMAT) : result;
};

export const getCurrentDate = (isFormat = true) =>
  isFormat ? dayjsWithTimezone().format(YYYY_MM_DD_FORMAT) : dayjsWithTimezone();

export const formatDisplayDateSummary = (inputDate: string): string =>
  // time format 21 Jan 2022, 01:15 PM
  dayjsWithTimezone(inputDate).format(DD_MMM_YYYY_HH_MM_A_FORMAT);

export const getDateByMonthNameFormat = (date: Date | null) => {
  if (!date) return null;
  const day = formattedDateDisplay(date, DD_FORMAT);
  const month = formattedDateDisplay(date, MMM_FORMAT);
  const year = formattedDateDisplay(date, YYYY_FORMAT);

  return {
    day,
    month,
    year,
  };
};

export const formatDateToDesiredFormat = (date: ConfigType | string, format = DD_MMM_YYYY_FORMAT) =>
  formattedDateDisplay(date, format);

export const getDayOfWeekByNumber = (dayNumber: number) => {
  if (dayNumber < 0 || dayNumber > 6) return null;

  const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return shortDays[dayNumber];
};

export const getDayOfWeekByDate = (date: string) => dayjsWithTimezone(date).format(DDD_FORMAT);

export const getHourFormat = (date?: string) => (date ? dayjsWithTimezone(date).format(HH_MM_A_FORMAT) : null);

export const sortByDateString = ({ ascending, dateA, dateB }: SortByDateStringProps): number => {
  const convertedDateA = dayjsWithTimezone(dateA).valueOf();
  const convertedDateB = dayjsWithTimezone(dateB).valueOf();

  return ascending ? convertedDateA - convertedDateB : convertedDateB - convertedDateA;
};

export const compareIsoDate = (dateA: ConfigType, dateB: ConfigType) =>
  dayjsWithTimezone(dateA).format(YYYY_MM_DD_FORMAT) === dayjsWithTimezone(dateB).format(YYYY_MM_DD_FORMAT);

export const getTimeOfDate = (date: ConfigType) => dayjsWithTimezone(date).format(HH_MM_A_FORMAT);

export const getCurrentYear = () => dayjsWithTimezone().year();

export const areInSameYear = (a?: string, b?: string) =>
  !!a && !!b && dayjsWithTimezone(a).year() === dayjsWithTimezone(b).year();

export const isBeforeDate = (date: ConfigType, compareDate: ConfigType) =>
  dayjsWithTimezone(date).isBefore(dayjsWithTimezone(compareDate));

export const isAfterDate = (date: ConfigType, compareDate: ConfigType) =>
  dayjsWithTimezone(date).isAfter(dayjsWithTimezone(compareDate));
